import { approvalFilters } from "./approval-filters";
import { authorityQueryKey as key } from "./authority";
import type { ExperienceFilters, ReferenceFilters, ReferenceKind } from "./catalogue-models";
import type { InvitationFilters } from "./identity-models";
import type {
  ApprovalRequestFilterInput,
  AuditTrailFilters,
  PartnerFilters,
  UserFilters,
} from "./models";
import type {
  PartnerContactFilters,
  PartnerOfferingFilters,
  PartnerScheduleFilters,
} from "./partner-child-models";

export const queryKeys = {
  get sensitiveReadControls() {
    return key("sensitive-read-controls");
  },
  get effectiveActionPolicies() {
    return key("action-policies", "effective");
  },
  actionPolicies: (page: number, pageSize: number, actionCode?: string) =>
    key("action-policies", "list", page, pageSize, actionCode ?? "ALL"),
  executionControls: (page: number, pageSize: number) => key("execution-controls", page, pageSize),
  cataloguePage: (kind: ReferenceKind, filters: ReferenceFilters | ExperienceFilters) =>
    key(kind, "list", filters),
  catalogueDetail: (kind: ReferenceKind, id: string) => key(kind, "detail", id),
  catalogueLookup: (kind: ReferenceKind, filters: ReferenceFilters) => key(kind, "lookup", filters),
  get access() {
    return key("access");
  },
  get administrators() {
    return key("administrators");
  },
  get approvalPolicies() {
    return key("approval-policies");
  },
  approvalRequest: (requestId: string) => key("approvals", requestId),
  approvalRequests: (page: number, pageSize: number, filters?: ApprovalRequestFilterInput) =>
    key("approvals", "list", page, pageSize, approvalFilters(filters)),
  auditTrail: (filters: AuditTrailFilters) => key("audit-trail", filters),
  auditTrailDetail: (interactionId: string) => key("audit-trail", "detail", interactionId),
  get invitations() {
    return key("invitations");
  },
  invitationList: (filters: InvitationFilters) => key("invitations", "list", filters),
  get legal() {
    return key("legal");
  },
  legalDetail: (documentId: string) => key("legal", documentId),
  notifications: (page: number, pageSize: number) => key("notifications", page, pageSize),
  get destinations() {
    return key("destinations");
  },
  get experiences() {
    return key("experiences");
  },
  partner: (partnerId: string) => key("partners", partnerId),
  partnerTeam: (partnerId: string) => key("partners", partnerId, "team"),
  partnerOfferingExperienceIds: (partnerId: string) =>
    key("partners", partnerId, "offering-experience-ids"),
  partnerOffering: (offeringId: string) => key("partner-offerings", offeringId, "detail"),
  partnerContact: (contactId: string) => key("partners", "contact-detail", contactId),
  partnerContactErasurePreview: (contactId: string) =>
    key("partners", "contact-erasure-preview", contactId),
  partnerContactErasure: (requestId: string) => key("partners", "contact-erasure", requestId),
  partnerContactEvidenceHold: (contactId: string) =>
    key("partners", "contact-evidence-hold", contactId),
  partnerSchedule: (scheduleId: string) => key("partner-offerings", "schedule-detail", scheduleId),
  partnerContacts: (partnerId: string, filters?: PartnerContactFilters) =>
    key("partners", partnerId, "contacts", ...(filters ? [filters] : [])),
  partnerOfferings: (partnerId: string, filters?: PartnerOfferingFilters) =>
    key("partners", partnerId, "offerings", ...(filters ? [filters] : [])),
  partnerImport: (batchId: string) => key("partner-imports", batchId),
  partnerImportReview: (batchId: string, kind: string, filters: object, snapshotVersion?: number) =>
    key("partner-imports", batchId, "review", kind, filters, snapshotVersion ?? null),
  partnerImportChanges: (batchId: string) => key("partner-imports", batchId, "changes"),
  get partnerImportDefinition() {
    return key("partner-imports", "definition");
  },
  get partnerImportList() {
    return key("partner-imports", "list");
  },
  partnerImportIssues: (batchId: string) => key("partner-imports", batchId, "issues"),
  partnerImports: (page: number, pageSize: number) =>
    key("partner-imports", "list", page, pageSize),
  partnerSchedules: (offeringId: string, filters?: PartnerScheduleFilters) =>
    key("partner-offerings", offeringId, "schedules", ...(filters ? [filters] : [])),
  partners: (filters: PartnerFilters) => key("partners", "list", filters),
  get roles() {
    return key("roles");
  },
  user: (userId: string) => key("users", userId),
  userLegal: (userId: string) => key("users", userId, "legal"),
  userSessions: (userId: string) => key("users", userId, "sessions"),
  users: (filters: UserFilters) => key("users", filters),
};
